<template>
  <div
    v-if="visible"
    class="sos-popup-overlay"
    @click.self="close"
  >
    <div class="sos-popup">
      <div class="sos-popup__header">
        <h3 class="sos-popup__title">
          Search stats
        </h3>
        <button
          class="sos-popup__close"
          type="button"
          @click="close"
        >
          ×
        </button>
      </div>

      <div class="sos-popup__body">
        <!-- Loader overlay -->
        <div
          v-if="loading"
          class="sos-popup__loader"
        >
          <div class="sos-spinner" />
        </div>

        <template v-else>
          <div
            v-if="error"
            class="sos-popup__error"
          >
            {{ error }}
          </div>

          <div
            v-else
            class="sos-popup__content"
          >
            <div class="sos-popup__chart">
              <canvas ref="chartCanvas" />
            </div>

            <div class="sos-popup__titles">
              <div class="sos-popup__titles-header">
                <h4>Titles ({{ titles.length }})</h4>
              </div>
              <div class="sos-popup__titles-list">
                <ul>
                  <li
                    v-for="(title, index) in titles"
                    :key="index"
                    v-html="highlight(title)"
                  />
                </ul>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
  <div v-else />
</template>

<script>
import Chart from 'chart.js/auto';

export default {
  name: 'ShareOfShelfKeywordModal',

  props: {
    value: {
      type: Boolean,
      default: false
    },
    keywordId: {
      type: [Number, String],
      required: true
    }
  },

  data() {
    return {
      loading: false,
      error: null,
      chartInstance: null,
      labels: [],
      totalCounts: [],
      matchCounts: [],
      titles: [],
      brandName: null
    };
  },

  computed: {
    visible() {
      return this.value;
    }
  },

  watch: {
    visible(val) {
      if (val) {
        this.fetchData();
      } else {
        this.destroyChart();
      }
    },

    keywordId(newVal, oldVal) {
      if (this.visible && newVal !== oldVal) {
        this.fetchData();
      }
    }
  },

  beforeDestroy() {
    this.destroyChart();
  },

  methods: {
    close() {
      this.$emit('input', false);
    },

    async fetchData() {
      const id = this.keywordId;

      if (!id) return;

      // Prevent duplicate requests in the same tick
      if (this.loading) {
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        const { data } = await axios.get(`/share-of-shelf/get-keyword-chart/${id}`);

        const chart = data.chart || {};

        this.labels = chart.labels || [];
        this.totalCounts = chart.totalCounts || [];
        // Backend returns "matchCount"
        this.matchCounts = chart.matchCount || chart.matchCounts || [];
        this.titles = chart.titles || [];
        this.brandName = chart.brand;

        this.$nextTick(() => {
          this.renderChart();
        });
      } catch (e) {
        console.error(e);
        this.error = 'Failed to load keyword stats';
        this.destroyChart();
      } finally {
        this.loading = false;

        if (this.visible && !this.error) {
          this.$nextTick(() => {
            this.renderChart();
          });
        }
      }
    },

    renderChart() {
      if (!this.$refs.chartCanvas) {
        return;
      }

      const ctx = this.$refs.chartCanvas.getContext('2d');

      if (this.chartInstance) {
        this.chartInstance.destroy();
        this.chartInstance = null;
      }

      this.chartInstance = new Chart(ctx, {
        type: 'line',
        data: {
          labels: this.labels,
          datasets: [
            {
              label: 'Total keywords',
              data: this.totalCounts,
              fill: false
            },
            {
              label: 'Matches',
              data: this.matchCounts,
              fill: false
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true
            },
            tooltip: {
              mode: 'index',
              intersect: false
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Date'
              }
            },
            y: {
              title: {
                display: true,
                text: 'Count'
              },
              beginAtZero: true
            }
          }
        }
      });
    },

    destroyChart() {
      if (this.chartInstance) {
        this.chartInstance.destroy();
        this.chartInstance = null;
      }
    },

    highlight(text) {
      if (!this.brandName) return text;

      // Escape regex characters inside keyword
      const escaped = this.brandName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

      const reg = new RegExp(`(${escaped})`, 'gi');

      return text.replace(reg, '<span class="highlighted">$1</span>');
    },
  }
};
</script>

<style lang="scss" scoped>
.sos-popup-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.sos-popup {
  background: #ffffff;
  border-radius: 8px;
  width: 90%;
  max-width: 960px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}

.sos-popup__header {
  padding: 12px 16px;
  border-bottom: 1px solid #e3e3e3;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sos-popup__title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.sos-popup__close {
  border: none;
  background: transparent;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;

  &:hover {
    opacity: 0.7;
  }
}

.sos-popup__body {
  position: relative;
  display: flex;
  flex: 1 1 auto;
  padding: 16px;
  min-height: 300px;
}

.sos-popup__content {
  display: flex;
  flex: 1 1 auto;
  gap: 16px;
}

.sos-popup__chart {
  flex: 2 1 0;
  min-width: 0;
  height: 100%;
}

.sos-popup__titles {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.sos-popup__titles-header {
  margin-bottom: 8px;

  h4 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
  }
}

.sos-popup__titles-list {
  flex: 1 1 auto;
  overflow-y: auto;
  border: 1px solid #e3e3e3;
  border-radius: 4px;
  padding: 8px;

  ul {
    list-style: none;
    padding-left: 0;
    margin: 0;
  }

  li {
    font-size: 13px;
    line-height: 1.4;
    padding: 4px 0;
    border-bottom: 1px solid #f1f1f1;

    &:last-child {
      border-bottom: none;
    }

    ::v-deep .highlighted {
      background: #ffef9f;
      color: #000;
      padding: 2px 3px;
      border-radius: 3px;
    }
  }
}

/* Loader overlay */
.sos-popup__loader {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
}

.sos-spinner {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 4px solid #ccc;
  border-top-color: #007bff;
  animation: sos-spin 0.8s linear infinite;
}

@keyframes sos-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Error block */
.sos-popup__error {
  color: #c00;
  font-size: 14px;
  margin: auto;
}

/* Small screens: stack chart and titles */
@media (max-width: 768px) {
  .sos-popup__content {
    flex-direction: column;
  }

  .sos-popup__chart,
  .sos-popup__titles {
    flex: 1 1 0;
  }
}
</style>
